import { Component, effect, inject, signal, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { Dialog } from '@angular/cdk/dialog';
import { SnackbarComponent } from '@components/shared/snackbar/snackbar.component';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from '@components/shared/confirm-dialog/confirm-dialog.component';
import { appVersion, constantVariables } from '@shared/helper/configs';
import { GeneralService } from '@shared/services/general.service';
import { PushNotificationService } from '@shared/services/push-notification.service';
import { RouterService } from '@shared/services/router.service';
import { TokenService } from '@shared/services/token.service';
import { ProfileService } from '@shared/services/profile.service';
import { authorizeUser, init, setPhoneNumber } from '@metrixorg/websdk';

import { envs } from '@envs/environment';
import { UserService } from '@shared/services/user.service';

@Component({
  selector: 'app-root',
  providers: [RouterService, ProfileService],
  imports: [RouterOutlet, SnackbarComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  standalone: true,
})
export class AppComponent implements OnInit {
  private readonly generalService = inject(GeneralService);
  private readonly tokenService = inject(TokenService);
  private readonly pushNotificationService = inject(PushNotificationService);
  private readonly profileService = inject(ProfileService);
  private readonly dialog = inject(Dialog);
  private readonly routerService = inject(RouterService);
  private readonly userService = inject(UserService);

  private profileCheckStarted = false;

  appVersion = appVersion;
  showContent = signal(false);
  fromApp = signal(false);

  constructor() {
    effect(() => {
      const userInfo = this.tokenService.userInfo();
      this.checkProfileCompletion();
      this.checkUserPersonalInfoEntrance();

      if (userInfo) {
        this.pushNotificationService.requestSub();
        this.pushNotificationService.subscribeForNotifications();
      }
    });
  }

  ngOnInit(): void {
    this.handleVisitorId();
    this.userService.fetchWallet();

    init(envs.metrix.appId, envs.metrix.apiKey, {
      sessionTracking: {
        enabled: true,
        packageName: 'fbtv3-website',
      },
    });

    const mobile = this.tokenService.userInfo()?.mobile;

    if (mobile) {
      authorizeUser(mobile);
      setPhoneNumber(mobile);
    }
  }

  private checkUserPersonalInfoEntrance(): void {
    const user = this.tokenService.userInfo();
    if (!user) return;
    if (!user.firstName || !user.lastName) {
      this.routerService.navigateTo('/register/personal-info');
    }
  }

  private checkProfileCompletion(): void {
    const token = this.tokenService.getRtk();

    if (!token || this.profileCheckStarted) {
      return;
    }

    this.profileCheckStarted = true;

    this.profileService.checkUserCompleteProfile().subscribe({
      next: (res) => {
        const isProfileComplete = res.data?.checkUserCompleteProfile;

        if (isProfileComplete === false) {
          this.openProfileCompletionDialog();
        }
      },
      error: (error) => {
        console.error('Failed to check profile completion:', error);
      },
    });
  }

  private openProfileCompletionDialog(): void {
    const data: ConfirmDialogData = {
      title: 'تکمیل پروفایل',
      message: 'پروفایل شما هنوز تکمیل نشده است. آیا می‌خواهید آن را تکمیل کنید؟',
      okBtnText: 'تکمیل پروفایل',
      cancelBtnText: 'بستن',
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data,
    });

    dialogRef.closed.subscribe((result) => {
      if (result === 'accept') {
        this.routerService.navigateTo('/settings/profile');
      }
    });
  }

  private handleVisitorId(): void {
    if (this.generalService.isBrowserOnly) {
      if (!localStorage.getItem(constantVariables.visitorId)) {
        this.tokenService.getVisitorId().subscribe({
          next: () => {
            this.showContent.set(true);
          },
          error: () => {
            this.showContent.set(true);
          },
        });
      } else {
        this.showContent.set(true);
      }
    } else {
      this.showContent.set(true);
    }
  }
}
